import { Select, SelectItem, SelectOutlet } from "@swivl-mono/ui";
import React, { useEffect, useMemo } from "react";
import { IoIosArrowDown } from "react-icons/io";

import LocationsModel from "../../../../Models/Locations/Locations.Model";
import { LOCATION_SCOPE_TOOLTIP } from "../assigneeLocationScope";
import { FieldLabelWithInfo } from "./FieldLabelWithInfo";
import { TicketPropertyRow } from "./TicketPropertyRow";

type LocationFieldProps = {
  locationId: number | null;
  /** Server-resolved name, used when the location is outside the viewer's scope and so absent from the list (ENG-2941). */
  locationName?: string | null;
  /** Server-resolved group name for `locationName`, so out-of-scope locations read "Group: Location" like in-scope ones. */
  locationGroupName?: string | null;
  onChange: (locationId: number | null) => void;
  pending?: boolean;
  locked?: boolean;
};

const NO_LOCATION_VALUE = "__no_location__";

export function LocationField({ locationId, locationName, locationGroupName, onChange, pending, locked }: LocationFieldProps) {
  const { groups, locations } = LocationsModel.useState((s) => s);

  useEffect(() => {
    if (locked) return;
    void LocationsModel.getGroupsAndLocations(false);
  }, [locked]);

  const displayText = useMemo(() => {
    if (!locationId) return "No location";
    const location = locations?.find((l) => l.id === locationId);
    if (location) {
      const group = groups?.find((g) => g.id === location.groupId);
      return group ? `${group.name}: ${location.name}` : location.name;
    }
    // Not in the list: the location is outside the viewer's scope, so fall back
    // to the name the server resolved for us (ENG-2941).
    if (locationName) return locationGroupName ? `${locationGroupName}: ${locationName}` : locationName;
    return `Location #${locationId}`;
  }, [locationId, locations, groups, locationName, locationGroupName]);

  const options: SelectItem[] = useMemo(() => {
    const result: SelectItem[] = [{ label: "None", value: NO_LOCATION_VALUE }, { type: "divider" as const }];

    if (!groups?.length) return result;

    groups.forEach((group, groupIndex) => {
      if (groupIndex > 0) {
        result.push({ type: "divider" as const });
      }
      result.push({
        type: "group" as const,
        label: group.name,
        options: group.locations?.map((location) => ({ label: location.name, value: String(location.id) })) ?? []
      });
    });

    return result;
  }, [groups]);

  const handleChange = (selected: string[]): void => {
    const val = selected[0];
    if (!val || val === NO_LOCATION_VALUE) {
      onChange(null);
    } else {
      onChange(parseInt(val));
    }
  };

  if (locked) {
    return (
      <TicketPropertyRow label="Location" lockedAction="Updating locations">
        {displayText}
      </TicketPropertyRow>
    );
  }

  return (
    <TicketPropertyRow label={<FieldLabelWithInfo label="Location" tooltip={LOCATION_SCOPE_TOOLTIP} />} pending={pending}>
      <Select
        options={options}
        selectedValues={locationId ? [String(locationId)] : [NO_LOCATION_VALUE]}
        onChange={handleChange}
        hasSearchInDropdown
        styles={{ [SelectOutlet.POPOVER]: { minWidth: 240, width: "auto" } }}
        customTrigger={({ isOpen }) => (
          <button className={`flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-sm ${isOpen ? "bg-grey-100" : "hover:bg-grey-50"}`}>
            {displayText}
            <IoIosArrowDown className={`h-3.5 w-3.5 text-grey-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      />
    </TicketPropertyRow>
  );
}
